import { type FC } from "react";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";
import { useSendContractPaymentInvoiceMutation } from "@/admin/entities/billing";
import {
  getErrorMessage,
  getGraphQLErrorCode,
} from "@/shared/utils/errorCodes";
import { formatDay } from "./billingFormat";

interface SendInvoiceButtonProps {
  paymentId: string;
  sentAt?: string | null;
  sentTo: string[];
  /** Зовётся после успешной мутации: карточка платежа перезапрашивает себя и список. */
  onCompleted?: () => void;
}

/**
 * «Отправить письмо» видно при выставленном счёте, кроме оплаченного и
 * отменённого платежа (спека §5, гейт в ContractPaymentActions.canSend).
 * Повторная отправка не создаёт новый номер счёта, только письмо со
 * ссылкой на уже выставленный, и уходит она заново всем адресам из
 * реквизитов организации, включая уже получивших. Под кнопкой подпись с
 * датой последней отправки и адресами, если письмо уже уходило.
 */
export const SendInvoiceButton: FC<SendInvoiceButtonProps> = ({
  paymentId,
  sentAt,
  sentTo,
  onCompleted,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [sendInvoice, { loading }] = useSendContractPaymentInvoiceMutation({
    onCompleted: () => {
      // Успех мутации означает, что письмо ушло хотя бы на один адрес:
      // пустой список получателей бэкенд отвергает кодом
      // CONTRACT_PAYMENT_INVOICE_NO_RECIPIENTS, а частичный отказ приходит
      // кодом CONTRACT_PAYMENT_INVOICE_MAIL_FAILED. И то, и другое ловит
      // onError, поэтому проверять sentTo из ответа здесь не нужно.
      enqueueSnackbar("Письмо отправлено", { variant: "success" });
      onCompleted?.();
    },
    onError: (error) => {
      enqueueSnackbar(
        getErrorMessage(getGraphQLErrorCode(error), error.message),
        { variant: "error" }
      );
    },
  });

  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Button
        size="small"
        disabled={loading}
        onClick={(event) => {
          event.stopPropagation();
          void sendInvoice({ variables: { id: paymentId } });
        }}
      >
        Отправить письмо
      </Button>
      {sentAt && (
        <Typography variant="caption" color="text.secondary">
          Отправлено {formatDay(sentAt)} на {sentTo.join(", ")}
        </Typography>
      )}
    </Stack>
  );
};
